import React, { useEffect, useMemo, useState } from "react";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Checkbox, FormGroup, makeStyles } from "@material-ui/core";
import { observer } from "mobx-react-lite";
import { OptionSelector } from "./OptionSelector";
import { runInAction, toJS } from "mobx";
import { deepClone } from "../../../helpers/objectHelper";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckboxDefaultIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import RadioButtonDefaultIcon from "@material-ui/icons/RadioButtonUncheckedOutlined";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";

const useStyles = makeStyles((theme) => ({
  root: {
    "&:hover": {
      backgroundColor: "transparent !important",
    },
    marginLeft: 0,
    marginRight: 0,
  },
  label: {
    "&.Mui-disabled": {
      color: "inherit !important",
    },
    color: "inherit",
    fontWeight: "inherit",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
}));

export const Option = observer((props) => {
  const classes = useStyles();
  const [initValidation, setInitValidation] = useState(true);
  const [runValidation, setRunValidation] = useState(false);

  const orders = toJS(props.orders);
  const { settings, environment, updateOrder, activePersonNo, validity, question, validate, contentLayoutModuleId, checkConditions } = props;
  const { prefixClassName, displayType, hidden, readonly, required } = settings;

  const options = Array.from(question.questionOption);
  const answer = (orders[activePersonNo] && orders[activePersonNo].answer && orders[activePersonNo]?.answer?.find((a) => a.questionId == question.questionId)) || {};

  useEffect(() => {
    if (orders && orders[activePersonNo]?.badgeNo && initValidation) {
      setInitValidation(false);
      validateOptions();
    }
  }, [orders, activePersonNo, initValidation]);

  useEffect(() => {
    if (answer?.answerRow) {
      validateOptions();
    }
  }, [answer]);

  useEffect(() => {
    if (runValidation) {
      setRunValidation(false);
      validateOptions();
    }
  }, [runValidation]);

  const handleChange = (value, optionId) => {
    let order = deepClone(orders[activePersonNo]);

    if (!order.answer) {
      order.answer = [];
    }
    let answer = order.answer.find((a) => a.questionId == question.questionId);
    if (!answer) {
      answer = {
        answerId: 0,
        interactionKey: null,
        orderId: 0,
        questionId: question.questionId,
        answerRow: [],
      };
      order.answer.push(answer);
    }
    if (!answer.answerRow) {
      answer.answerRow = [];
    }
    if (value === true && question.requiredMax === 1) {
      answer.answerRow.map((ar) => {
        ar.deleted = true;
      });
    }
    let row = answer.answerRow.find((ar) => ar.questionOptionId == optionId);
    if (row) {
      row.deleted = !value;
    } else {
      answer.answerRow.push({
        answerId: answer.answerId,
        answerRowId: 0,
        questionOptionId: optionId,
        text: null,
        deleted: false,
      });
    }
    answer.answerRow = answer.answerRow.filter((ar) => {
      return !ar.deleted;
    });
    order.answer[order.answer.indexOf(order.answer.find((a) => a.questionId == question.questionId))] = { ...answer };

    updateOrder(order);
    setRunValidation(true);
    checkConditions(contentLayoutModuleId);
  };

  const validateOptions = () => {
    let isValid = true;
    let reason = "";
    let { requiredMax, requiredMin } = question;
    const checkedCount = getCheckedCount();
    if (readonly || hidden) {
      isValid = true;
      return isValid;
    } else if (required && checkedCount == 0) {
      isValid = false;
      reason = "required";
    } else if (requiredMin > 0 && checkedCount > 0 && checkedCount < requiredMin) {
      isValid = false;
      reason = "minLengthOption";
    } else if (requiredMax > 0 && checkedCount > 0 && checkedCount > requiredMax) {
      isValid = false;
      reason = "maxLengthOption";
    }
    runInAction(() => {
      validity.valid = isValid;
      validity.reason = reason;
    });
  };

  const getCheckedCount = () => {
    if (orders.length > 0) {
      let order = orders[activePersonNo];
      if (order.answer) {
        let answer = order.answer.find((a) => a.questionId == question.questionId);
        if (answer && answer.answerRow && answer.answerRow.length > 0) {
          let rows = answer.answerRow.filter((ar) => ar.questionOptionId != 0 && !ar.deleted);
          return rows.length;
        }
      }
    }
    return 0;
  };

  useEffect(() => {
    if (orders[activePersonNo]?.badgeNo) {
      validateOptions();
    }
  }, [orders, validateOptions]);

  const isValid = useMemo(() => {
    const checkedCount = getCheckedCount();
    if (checkedCount.length > 0 && validity?.valid === false) return false;
    else if (checkedCount.length > 0 && validity?.valid === true) return true;
    else if (validate && validity?.valid === false) return false;
    return undefined;
  }, [validity, validate, getCheckedCount]);

  if (displayType == "radio") {
    return (
      <RadioGroup
        className={`${prefixClassName} checkbox-group`}
        onChange={(e) => {
          handleChange(true, parseInt(e.target.value));
        }}
      >
        {options
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((qo) => (
            <FormControlLabel
              className="option"
              classes={classes}
              disabled={readonly}
              checked={Boolean(answer?.answerRow?.find((ar) => ar.questionOptionId == qo.questionOptionId))}
              key={qo.questionOptionId}
              control={
                <Radio
                  classes={{ root: classes.root }}
                  disableRipple
                  icon={<RadioButtonDefaultIcon style={{ color: isValid === false ? "#d53443" : undefined, marginLeft: "-3px" }} className="checkbox cms-checkbox" />}
                  checkedIcon={<RadioButtonCheckedIcon className="checkbox cms-checkbox" style={{ marginLeft: "-3px" }} />}
                />
              }
              value={qo.questionOptionId.toString()}
              label={qo?.questionOptionLanguage?.find((fl) => fl.languageCode == environment.lang)?.label}
            />
          ))}
      </RadioGroup>
    );
  } else if (displayType == "select") {
    return <OptionSelector {...{ ...props, ...settings, handleChange, valid: isValid, options }} />;
  } else {
    return (
      <FormGroup className={`${prefixClassName} checkbox-group`}>
        {options
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((qo) => (
            <FormControlLabel
              className="option"
              classes={classes}
              key={qo.questionOptionId}
              disabled={readonly}
              onChange={(e) => handleChange(e.target.checked, qo.questionOptionId)}
              checked={Boolean(answer?.answerRow?.find((ar) => ar.questionOptionId == qo.questionOptionId))}
              control={
                <Checkbox
                  classes={{ root: classes.root }}
                  disableRipple
                  icon={<CheckboxDefaultIcon style={{ color: isValid === false ? "#d53443" : undefined, marginLeft: "-3px" }} className="checkbox cms-checkbox" />}
                  checkedIcon={<CheckBoxIcon className="checkbox cms-checkbox" style={{ marginLeft: "-3px" }} />}
                />
              }
              label={<p>{qo?.questionOptionLanguage?.find((fl) => fl.languageCode == environment.lang)?.label || "-"}</p>}
            />
          ))}
      </FormGroup>
    );
  }
});
